import { portalAction, portalScreenContext } from '../../../app/telemetry/portalTelemetry';
import { usePortalScreen } from '../../../app/telemetry/usePortalTelemetry';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { Box, Link, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { ApiRequestStatus } from "../../../app/enums/apiRequestStatus";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { getCalendarEventInstanceByIdAsync } from "../../../app/slices/calendarSlice";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import { MESSAGE_PROCESSING_DETAILS } from "../../../app/utils/constant";

interface RouteParams { instanceId: string; }

function formatDate(value?: string | Date | null) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-IE', { day: '2-digit', month: 'long', year: 'numeric' });
}

function externalUrl(value: string) {
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

interface DetailRow {
    label: string;
    value: ReactNode;
    icon: ReactNode;
}

export default function CalendarEventDetailsPage() {
    const { instanceId } = useParams<RouteParams>();
    const dispatch = useAppDispatch();
    const { calendarOfActivitiesInstance: event, calendarOfActivitiesInstanceStatus: status } = useAppSelector(state => state.calendar);

    useEffect(() => {
        if (instanceId) dispatch(getCalendarEventInstanceByIdAsync(instanceId));
    }, [dispatch, instanceId]);

    usePortalScreen(portalScreenContext('CalendarEventDetailsPage'), undefined, !((status === ApiRequestStatus.Idle || status === ApiRequestStatus.Pending)));

    if (status === ApiRequestStatus.Idle || status === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />;
    }

    if (status === ApiRequestStatus.Rejected || !event || event.eventInstanceId?.toLowerCase() !== instanceId.toLowerCase()) {
        return (
            <Box sx={{ px: { xs: 2.5, sm: 5 }, py: 4 }}>
                <Typography component="h1" sx={{ color: '#252d38', fontSize: 32, mb: 2 }}>Event unavailable</Typography>
                <Typography sx={{ mb: 3 }}>The event details could not be loaded.</Typography>
            </Box>
        );
    }

    const eventUrl = event.eventUrl ? externalUrl(event.eventUrl) : null;
    const details: DetailRow[] = [
        { label: 'Date', value: formatDate(event.startDate), icon: <CalendarMonthOutlinedIcon /> },
        { label: 'Location', value: event.venue || '-', icon: <LocationOnOutlinedIcon /> },
        { label: 'CPD credits', value: event.credits?.toString() ?? '-', icon: <SchoolOutlinedIcon /> },
        {
            label: 'Contact',
            value: event.contactEmail ? <Link href={`mailto:${event.contactEmail}`} onClick={portalAction(portalScreenContext('CalendarEventDetailsPage'), 'CalendarEventDetailsPage.navigate', () => {})}>{event.contactEmail}</Link> : '-',
            icon: <EmailOutlinedIcon />,
        },
        {
            label: 'More information',
            value: eventUrl ? <Link href={eventUrl} target="_blank" rel="noopener noreferrer" onClick={portalAction(portalScreenContext('CalendarEventDetailsPage'), 'CalendarEventDetailsPage.navigate', () => {})}>Visit event website <OpenInNewIcon sx={{ ml: 0.5, fontSize: 16, verticalAlign: 'text-bottom' }} /></Link> : '-',
            icon: <OpenInNewIcon />,
        },
    ];

    return (
        <Box component="main" sx={{ bgcolor: '#fff', minHeight: 650, px: { xs: 2.5, sm: 4.5 }, py: { xs: 2.5, sm: 3 } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', width: '100%' }}>
                <Box>
                    <Typography sx={{ color: '#303844', fontSize: 13, fontWeight: 500, textTransform: 'uppercase', mb: 1 }}>
                        PCS Event
                    </Typography>
                    <Box aria-hidden="true" sx={{ width: 48, borderTop: '3px solid', borderColor: 'primary.main', mb: 2 }} />
                    <Typography component="h1" sx={{ color: '#172334', fontSize: { xs: 34, sm: 43 }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.05, mb: 2 }}>
                        {event.description || 'Event details'}
                    </Typography>

                    <Box sx={{ border: '1px solid #dde2e7', borderRadius: 2, px: { xs: 2, sm: 3 }, py: 2.25, boxShadow: '0 8px 22px rgba(25, 39, 58, 0.04)', mb: 3 }}>
                        <Typography component="h2" sx={{ color: '#172334', fontSize: 18, fontWeight: 700, mb: 1 }}>
                            Event details
                        </Typography>
                        <Box component="dl" sx={{ m: 0 }}>
                            {details.map((detail, index) => (
                                <Box key={detail.label} sx={{ display: 'grid', gridTemplateColumns: { xs: '34px 100px minmax(0, 1fr)', sm: '40px 160px minmax(0, 1fr)' }, alignItems: 'center', minHeight: 45, borderTop: index === 0 ? 0 : '1px solid #dde2e7' }}>
                                    <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: '#fff0f3', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 18 } }}>
                                        {detail.icon}
                                    </Box>
                                    <Typography component="dt" sx={{ color: '#273345', fontSize: 14, fontWeight: 700 }}>{detail.label}</Typography>
                                    <Typography component="dd" sx={{ color: '#526071', fontSize: 14, m: 0, overflowWrap: 'anywhere', '& a': { color: 'primary.main' } }}>{detail.value}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
